import React from 'react';
import { useOnboarding } from '../../context/OnboardingContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

/**
 * Wraps any element with a pulsing green beacon dot when the onboarding
 * item is incomplete. Hovering the dot shows a tooltip with the item's
 * label and description. Uses Radix UI Tooltip for automatic viewport
 * collision avoidance — tooltip will never appear off-screen.
 *
 * tooltipSide: "top" (default) | "right" | "left" | "bottom"
 */
const OnboardingBeacon = ({ itemKey, children, className = '', tooltipSide = 'top' }) => {
  const { isComplete, isInChecklist, checklist } = useOnboarding();
  const show = isInChecklist(itemKey) && !isComplete(itemKey);

  if (!show) return children;

  const item = checklist.find(i => i.key === itemKey);
  const label = item?.label || 'Complete this step';
  const description = item?.description || '';

  return (
    <span className={`relative inline-flex ${className}`}>
      {children}

      {/* Beacon dot — positioned at top-right corner inside the element */}
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="absolute top-0 right-0 flex h-3 w-3 z-10 cursor-default">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-70" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-primary border-2 border-white" />
            </span>
          </TooltipTrigger>
          <TooltipContent
            side={tooltipSide}
            sideOffset={8}
            collisionPadding={12}
            className="bg-primary text-white border-0 max-w-[200px] p-0"
          >
            <div className="px-3 py-2">
              <p className="text-[9px] text-white/60 uppercase tracking-wide font-normal mb-0.5">Getting Started</p>
              <p className="text-xs font-semibold leading-snug">{label}</p>
              {description && <p className="text-[10px] text-white/80 mt-1 leading-snug">{description}</p>}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </span>
  );
};

export default OnboardingBeacon;
